import React, { useState, useEffect } from 'react';
import { Box, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';

interface SlideUpCardProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

const SlideUpCard: React.FC<SlideUpCardProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      // Trigger animation after component is rendered
      setTimeout(() => setIsVisible(true), 10);
      
      // Cleanup function
      return () => {
        // Restore body scroll
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    } else {
      setIsVisible(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    // Wait for animation to complete before calling onClose
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!shouldRender) return null;

  return (
    <>
      {/* Backdrop */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
        onClick={handleClose}
      />
      
      {/* Slide Up Card */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 100,
          left: '50%',
          transform: isVisible 
            ? 'translateX(-50%) translateY(0)' 
            : 'translateX(-50%) translateY(100%)',
          width: '90%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          backgroundColor: 'transparent',
          zIndex: 9999,
          transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
          opacity: isVisible ? 1 : 0,
          overflow: 'hidden',
        }}
      >
        {/* Close Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: 1,
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Card Content */}
        <Box
          sx={{
            maxHeight: 'calc(90vh - 60px)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(96, 165, 250, 0.5)',
              borderRadius: '4px',
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </>
  );
};

export default SlideUpCard;